import { useState } from 'react';
import {
  Box, Checkbox, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import type { ColumnType, TargetColumn } from './types';
import LookupConfigDialog from './LookupConfigDialog';

const TYPES: ColumnType[] = [
  'string', 'text', 'int', 'bigint', 'decimal', 'float',
  'boolean', 'date', 'datetime',
];

export interface TargetMappingProps {
  columns: TargetColumn[];
  keyColumns: string[];
  onChangeColumns: (next: TargetColumn[]) => void;
  onChangeKeys: (next: string[]) => void;
  /** Whether the column-editing feature is on (shows the Editable column). */
  columnEditing?: boolean;
  /** Why key columns are unavailable, when they are (a file-format analytics
      target is rewritten whole each run, so there is nothing to match on).
      Set = the Key boxes are disabled and the reason is shown. */
  keysDisabledReason?: string;
  /** 'all' = every column editable; otherwise list of editable source names. */
  editableColumns?: 'all' | string[];
  onChangeEditable?: (next: 'all' | string[]) => void;
}

export default function TargetMapping({
  columns, keyColumns, onChangeColumns, onChangeKeys,
  columnEditing, editableColumns, onChangeEditable, keysDisabledReason,
}: TargetMappingProps) {
  // Index of the column whose lookup is being configured (null = dialog shut).
  const [lookupIdx, setLookupIdx] = useState<number | null>(null);
  // The grid column edited is the source column (what the preview shows and
  // what persist reads), defaulting to the target name when source is blank.
  const srcOf = (c: TargetColumn) => (c.source?.trim() || c.name).trim().toLowerCase();
  const editableAll = editableColumns === 'all';
  const editableList = Array.isArray(editableColumns) ? editableColumns : [];
  const isEditable = (c: TargetColumn) => editableAll || editableList.includes(srcOf(c));
  const toggleEditable = (c: TargetColumn, on: boolean) => {
    if (editableAll || !onChangeEditable) return;
    const src = srcOf(c);
    if (!src) return;
    onChangeEditable(on
      ? [...new Set([...editableList, src])]
      : editableList.filter((s) => s !== src));
  };
  const colCount = columnEditing ? 7 : 6;
  const setRow = (i: number, patch: Partial<TargetColumn>) => {
    const prev = columns[i];
    const updated = { ...prev, ...patch };
    const next = columns.slice();
    next[i] = updated;
    onChangeColumns(next);
    // key_columns and editableColumns both reference the SOURCE name (srcOf) --
    // a key identifies a source column and is only resolved to its mapped
    // target column at persist time. So when a rename changes srcOf (a source
    // edit, or a name edit while source is blank, since srcOf falls back to the
    // name), follow it in both lists so the saved JSON never strands an old
    // name. The parent updates `proc` via functional setState, so these extra
    // onChange* calls compose with onChangeColumns instead of clobbering it.
    const oldSrc = srcOf(prev);
    const newSrc = srcOf(updated);
    if (oldSrc !== newSrc) {
      if (keyColumns.includes(oldSrc)) {
        onChangeKeys(keyColumns.map((k) => (k === oldSrc ? newSrc : k)));
      }
      if (onChangeEditable && Array.isArray(editableColumns) && editableColumns.includes(oldSrc)) {
        onChangeEditable(editableColumns.map((s) => (s === oldSrc ? newSrc : s)));
      }
    }
  };
  const addRow = () =>
    onChangeColumns([...columns, { name: '', type: 'string', nullable: true, source: '' }]);
  const removeRow = (i: number) => {
    const removed = columns[i];
    const next = columns.slice();
    next.splice(i, 1);
    onChangeColumns(next);
    // Both key_columns and editableColumns are keyed by the SOURCE name (srcOf),
    // so drop the removed column from each by its source name.
    const s = removed ? srcOf(removed) : '';
    if (s && keyColumns.includes(s)) {
      onChangeKeys(keyColumns.filter((k) => k !== s));
    }
    if (s && onChangeEditable && Array.isArray(editableColumns) && editableColumns.includes(s)) {
      onChangeEditable(editableColumns.filter((x) => x !== s));
    }
  };

  const toggleKey = (col: string, on: boolean) => {
    if (on && !keyColumns.includes(col)) onChangeKeys([...keyColumns, col]);
    if (!on) onChangeKeys(keyColumns.filter((k) => k !== col));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="subtitle2">Target columns</Typography>
        <Tooltip title="These define the target table. If the table does not exist it will be created on first persist. The 'Source' field is the column name from your SQL/calc output to copy from; leave blank to match by name.">
          <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={addRow} color="primary" aria-label="add column">
          <AddIcon />
        </IconButton>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="24%">Target column</TableCell>
            <TableCell width="16%">Type</TableCell>
            <TableCell width="24%">Source column</TableCell>
            <TableCell width="10%" align="center">Nullable</TableCell>
            <TableCell width="10%" align="center">Key</TableCell>
            {columnEditing && <TableCell width="10%" align="center">Editable</TableCell>}
            <TableCell width={48}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {columns.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} align="center" sx={{ color: 'text.secondary' }}>
                No target columns. Click <strong>+</strong> to add one.
              </TableCell>
            </TableRow>
          )}
          {columns.map((c, i) => (
            <TableRow key={i}>
              <TableCell>
                <TextField
                  size="small" fullWidth value={c.name}
                  onChange={(e) => setRow(i, { name: e.target.value })}
                  placeholder="invoice_amount"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small" fullWidth select value={c.type}
                  onChange={(e) => setRow(i, { type: e.target.value as ColumnType })}
                >
                  {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField
                  size="small" fullWidth value={c.source || ''}
                  onChange={(e) => setRow(i, { source: e.target.value })}
                  placeholder={c.name || '(same as target)'}
                />
              </TableCell>
              <TableCell align="center">
                <Checkbox
                  size="small" checked={c.nullable !== false}
                  onChange={(e) => setRow(i, { nullable: e.target.checked })}
                />
              </TableCell>
              <TableCell align="center">
                {/* A key identifies the SOURCE column (srcOf); persist resolves
                    it to the mapped target column at write time. So the box
                    reflects and toggles the source name, not the target name. */}
                <Tooltip title={keysDisabledReason || ''}>
                  <span>
                    <Checkbox
                      size="small"
                      checked={!keysDisabledReason && keyColumns.includes(srcOf(c))}
                      disabled={!srcOf(c) || !!keysDisabledReason}
                      onChange={(e) => toggleKey(srcOf(c), e.target.checked)}
                    />
                  </span>
                </Tooltip>
              </TableCell>
              {columnEditing && (
                <TableCell align="center">
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
                    <Tooltip title={editableAll ? 'All columns are editable' : 'Allow editing this column in preview'}>
                      <span>
                        <Checkbox
                          size="small"
                          checked={isEditable(c)}
                          disabled={editableAll || !c.name}
                          onChange={(e) => toggleEditable(c, e.target.checked)}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={c.lookup ? `Lookup: ${c.lookup.table}(${c.lookup.value_column})` : 'Configure a reference-table lookup for this column'}>
                      <span>
                        <IconButton
                          size="small"
                          color={c.lookup ? 'primary' : 'default'}
                          disabled={!isEditable(c)}
                          onClick={() => setLookupIdx(i)}
                          aria-label="configure lookup"
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              )}
              <TableCell>
                <IconButton size="small" onClick={() => removeRow(i)} aria-label="delete row">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {lookupIdx != null && columns[lookupIdx] && (
        <LookupConfigDialog
          key={lookupIdx}
          name={columns[lookupIdx].name || `column ${lookupIdx + 1}`}
          value={columns[lookupIdx].lookup}
          onClose={() => setLookupIdx(null)}
          onSave={(lk) => { setRow(lookupIdx, { lookup: lk }); setLookupIdx(null); }}
          onClear={() => { setRow(lookupIdx, { lookup: undefined }); setLookupIdx(null); }}
        />
      )}
    </Box>
  );
}
